import type { GutterId } from "./gutters.ts";
import type { ProfileId } from "./materials.ts";
import type { RoofAdditionId } from "./journey.ts";

export const SAMPLE_REQUEST_EVENT = "roofviz:sample-requested";
export const ADDITION_REQUEST_EVENT = "roofviz:addition-requested";

export interface RoofSampleRequestV1 {
  version: "roof_sample_request_v1";
  propertyTrackingId: string;
  profile: ProfileId;
  colour: string;
  requestedAt: string;
  source: "homeowner_stated";
  delivery: "not_arranged";
}

export interface RoofAdditionInterestV1 {
  version: "roof_addition_interest_v1";
  propertyTrackingId: string;
  addition: RoofAdditionId;
  gutter?: GutterId;
  requestedAt: string;
  source: "homeowner_stated";
  assessmentStatus: "not_requested";
}

export function sampleRequestStorageKey(trackingId: string): string {
  return `roofviz:sample-request:${trackingId}`;
}

export function additionInterestStorageKey(trackingId: string, addition: RoofAdditionId): string {
  return `roofviz:addition-interest:${trackingId}:${addition}`;
}

export function createSampleRequest(
  propertyTrackingId: string,
  profile: ProfileId,
  colour: string,
  now = new Date(),
): RoofSampleRequestV1 {
  return {
    version: "roof_sample_request_v1",
    propertyTrackingId,
    profile,
    colour,
    requestedAt: now.toISOString(),
    source: "homeowner_stated",
    delivery: "not_arranged",
  };
}

export function createAdditionInterest(
  propertyTrackingId: string,
  addition: RoofAdditionId,
  gutter?: GutterId,
  now = new Date(),
): RoofAdditionInterestV1 {
  return {
    version: "roof_addition_interest_v1",
    propertyTrackingId,
    addition,
    ...(addition === "gutters" && gutter ? { gutter } : {}),
    requestedAt: now.toISOString(),
    source: "homeowner_stated",
    assessmentStatus: "not_requested",
  };
}

export function serializeInterest(value: RoofSampleRequestV1 | RoofAdditionInterestV1): string {
  return JSON.stringify(value);
}

export function parseSampleRequest(value: string | null, trackingId: string): RoofSampleRequestV1 | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as Partial<RoofSampleRequestV1>;
    if (
      parsed.version !== "roof_sample_request_v1"
      || parsed.propertyTrackingId !== trackingId
      || typeof parsed.profile !== "string"
      || typeof parsed.colour !== "string"
      || typeof parsed.requestedAt !== "string"
      || parsed.source !== "homeowner_stated"
      || parsed.delivery !== "not_arranged"
      || !Number.isFinite(Date.parse(parsed.requestedAt))
    ) return null;
    return parsed as RoofSampleRequestV1;
  } catch {
    return null;
  }
}

export function parseAdditionInterest(
  value: string | null,
  trackingId: string,
  addition: RoofAdditionId,
): RoofAdditionInterestV1 | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as Partial<RoofAdditionInterestV1>;
    if (
      parsed.version !== "roof_addition_interest_v1"
      || parsed.propertyTrackingId !== trackingId
      || parsed.addition !== addition
      || typeof parsed.requestedAt !== "string"
      || parsed.source !== "homeowner_stated"
      || parsed.assessmentStatus !== "not_requested"
      || !Number.isFinite(Date.parse(parsed.requestedAt))
    ) return null;
    return parsed as RoofAdditionInterestV1;
  } catch {
    return null;
  }
}
